import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import {
  applyWidgetFilters,
  calcClosedTradesMetrics,
  computeCumulativeProfitFactorSeries,
  computeCumulativeWinRateSeries,
  DEFAULT_WIDGET_FILTER,
  formatFilterCriteria,
  type ClosedTradeForMetrics,
  type CumulativeProfitFactorPoint,
  type CumulativeWinRatePoint,
  type WidgetFilterSettings,
} from '../lib/tradeCalculations'
import {
  getDisplayCurrency,
  convertPnl,
} from '../hooks/useDashboardCurrency'
import type { FilterableMetricWidgetId } from '../config/dashboardWidgets'
import DashboardWidgetTitle from './DashboardWidgetTitle'

export type MetricWidgetType = FilterableMetricWidgetId

interface Strategy {
  id: string
  display_name: string
}

interface DashboardWidgetMetricProps {
  trades: ClosedTradeForMetrics[]
  filterSettings: WidgetFilterSettings | undefined
  metricType: MetricWidgetType
  selectedCurrency?: string
  exchangeRates?: Map<string, number>
  strategies?: Strategy[]
}

const cardClass =
  'h-full w-full dashboard-card p-3 md:p-4 flex flex-col justify-center'

/** Win rate & profit factor: headline + right-side sparkline */
const cardClassSparklineMetric =
  'h-full w-full dashboard-card p-3 md:p-4 flex flex-col min-h-0 justify-center'

export default function DashboardWidgetMetric({
  trades,
  filterSettings,
  metricType,
  selectedCurrency = 'USD',
  exchangeRates = new Map(),
  strategies = [],
}: DashboardWidgetMetricProps) {
  const { t } = useTranslation(['dashboard', 'trading'])
  const settings = filterSettings ?? DEFAULT_WIDGET_FILTER
  const filtered = useMemo(() => applyWidgetFilters(trades, settings), [trades, settings])
  const closedForCurrency = useMemo(
    () => filtered.filter((t) => t.exit_date && t.pnl != null && t.currency),
    [filtered]
  )
  const displayCurrency = useMemo(
    () => getDisplayCurrency(closedForCurrency, selectedCurrency),
    [closedForCurrency, selectedCurrency]
  )
  const tradesWithConvertedPnl = useMemo(() => {
    return filtered.map((t) => ({
      ...t,
      convertedPnl:
        t.exit_date && t.pnl != null && t.currency && t.currency !== displayCurrency
          ? convertPnl(t, displayCurrency, exchangeRates)
          : undefined,
    }))
  }, [filtered, displayCurrency, exchangeRates])
  const metrics = useMemo(
    () => calcClosedTradesMetrics(tradesWithConvertedPnl),
    [tradesWithConvertedPnl]
  )

  const isWinRateWidget = metricType === 'winRate'
  const isProfitFactorWidget = metricType === 'profitFactor'
  const isSparklineMetric = isWinRateWidget || isProfitFactorWidget
  const winRateSeries = useMemo(
    () => (isWinRateWidget ? computeCumulativeWinRateSeries(tradesWithConvertedPnl) : []),
    [isWinRateWidget, tradesWithConvertedPnl]
  )
  const profitFactorSeries = useMemo(
    () =>
      isProfitFactorWidget ? computeCumulativeProfitFactorSeries(tradesWithConvertedPnl) : [],
    [isProfitFactorWidget, tradesWithConvertedPnl]
  )

  const formatValue = () => {
    switch (metricType) {
      case 'totalPnl':
        return metrics.totalTrades > 0
          ? `${metrics.totalPnl >= 0 ? '+' : ''}${metrics.totalPnl.toFixed(2)} ${displayCurrency}`
          : '–'
      case 'totalTrades':
        return String(metrics.totalTrades)
      case 'winRate':
        return `${metrics.winRate.toFixed(1)}%`
      case 'profitFactor':
        return metrics.profitFactor === Infinity ? '∞' : metrics.profitFactor.toFixed(2)
      case 'avgPnlPercent':
        return metrics.avgPnlPct != null
          ? `${metrics.avgPnlPct >= 0 ? '+' : ''}${metrics.avgPnlPct.toFixed(2)}%`
          : '–'
      case 'weightedPnlPercent':
        return metrics.weightedPnlPct != null
          ? `${metrics.weightedPnlPct >= 0 ? '+' : ''}${metrics.weightedPnlPct.toFixed(2)}%`
          : '–'
      case 'expectancy':
        return metrics.expectancy != null
          ? `${metrics.expectancy >= 0 ? '+' : ''}${metrics.expectancy.toFixed(2)}R`
          : '–'
      case 'averageWin':
        if (metrics.totalTrades === 0) return '–'
        return metrics.avgWin > 0
          ? `+${metrics.avgWin.toFixed(2)} ${displayCurrency}`
          : `0.00 ${displayCurrency}`
      case 'averageLoss':
        if (metrics.totalTrades === 0) return '–'
        return metrics.avgLoss > 0
          ? `-${metrics.avgLoss.toFixed(2)} ${displayCurrency}`
          : `0.00 ${displayCurrency}`
      case 'averageRR':
        return metrics.avgRR > 0 ? metrics.avgRR.toFixed(2) : metrics.totalTrades > 0 ? '0.00' : '–'
      default:
        return '–'
    }
  }

  const valueClass = () => {
    if (metricType === 'totalPnl' && metrics.totalTrades > 0) {
      return metrics.totalPnl >= 0 ? 'text-green-600' : 'text-red-600'
    }
    if (metricType === 'avgPnlPercent' || metricType === 'weightedPnlPercent') {
      const val = metricType === 'avgPnlPercent' ? metrics.avgPnlPct : metrics.weightedPnlPct
      if (val == null) return 'text-gray-900'
      return val >= 0 ? 'text-green-600' : 'text-red-600'
    }
    if (metricType === 'expectancy' && metrics.expectancy != null) {
      return metrics.expectancy >= 0 ? 'text-green-600' : 'text-red-600'
    }
    if (metricType === 'averageWin') return 'text-green-600'
    if (metricType === 'averageLoss') return 'text-red-600'
    return 'text-gray-900'
  }

  const criteriaStr = formatFilterCriteria(settings, strategies)
  const titleClass =
    'text-xs font-medium uppercase tracking-wider text-gray-500'

  if (isSparklineMetric) {
    return (
      <div className={cardClassSparklineMetric}>
        <div className="flex min-h-0 min-w-0 w-full flex-col gap-1.5 md:gap-2">
          <div className="flex shrink-0 items-baseline justify-between gap-1">
            <p className={`truncate ${titleClass}`}>
              <DashboardWidgetTitle widgetId={metricType} />
            </p>
            {criteriaStr && (
              <span
                className="shrink-0 truncate text-[10px] text-gray-400"
                title={criteriaStr}
              >
                {criteriaStr}
              </span>
            )}
          </div>
          <div className="flex min-h-0 min-w-0 flex-row items-center gap-2 md:gap-4">
            <p className={`shrink-0 text-lg font-bold leading-none md:text-2xl ${valueClass()}`}>
              {formatValue()}
            </p>
            <div className="flex h-11 min-h-[44px] min-w-0 flex-1 items-stretch sm:h-12 md:h-14">
              {isWinRateWidget ? (
                <WinRateProgressionChart
                  points={winRateSeries}
                  label={t('dashboard:winRateProgressionChartAria')}
                />
              ) : (
                <ProfitFactorProgressionChart
                  points={profitFactorSeries}
                  label={t('dashboard:profitFactorProgressionChartAria')}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className={cardClass}>
      {/* Mobile: başlık solda, değer sağda (filtre göstergesi yok) */}
      <div className="flex flex-row items-center justify-between gap-2 py-0.5 md:hidden">
        <p className={`shrink-0 truncate ${titleClass}`}>
          <DashboardWidgetTitle widgetId={metricType} />
        </p>
        <p className={`shrink-0 text-base font-bold ${valueClass()}`}>{formatValue()}</p>
      </div>
      {/* Desktop: üstte başlık+filtre, altta değer */}
      <div className="hidden md:block">
        <div className="mb-1 flex items-baseline justify-between gap-1">
          <p className={`truncate ${titleClass}`}>
            <DashboardWidgetTitle widgetId={metricType} />
          </p>
          {criteriaStr && (
            <span
              className="shrink-0 truncate text-[10px] text-gray-400"
              title={criteriaStr}
            >
              {criteriaStr}
            </span>
          )}
        </div>
        <p className={`text-xl font-bold md:text-2xl ${valueClass()}`}>{formatValue()}</p>
      </div>
    </div>
  )
}

const CHART_W = 100
const CHART_H = 36
const CHART_PAD_X = 1.5
const CHART_PAD_Y = 3
/** Stroke width in viewBox units; kept thin when SVG is stretched. */
const CHART_STROKE_WIDTH = 1.05
/** 0..1, higher = more low-pass on Y (interior only; ends stay exact). */
const CHART_Y_SMOOTH_ALPHA = 0.28
/** Larger = looser cubic handles (less tight corners at knots). */
const CHART_CATMULL_DIVISOR = 11
/** Approximating path uses at most this many knots (plus smoothing). */
const CHART_PATH_MAX_KNOTS = 36

function softenChartCoords(
  coords: ReadonlyArray<{ x: number; y: number }>,
  alpha: number,
): { x: number; y: number }[] {
  if (coords.length <= 2) return coords.map((c) => ({ x: c.x, y: c.y }))
  const n = coords.length
  const ys = coords.map((c) => c.y)
  const fwd: number[] = new Array(n)
  fwd[0] = ys[0]!
  for (let i = 1; i < n; i++) {
    fwd[i] = alpha * ys[i]! + (1 - alpha) * fwd[i - 1]!
  }
  const bwd: number[] = new Array(n)
  bwd[n - 1] = ys[n - 1]!
  for (let i = n - 2; i >= 0; i--) {
    bwd[i] = alpha * ys[i]! + (1 - alpha) * bwd[i + 1]!
  }
  return coords.map((c, i) => {
    if (i === 0 || i === n - 1) return { x: c.x, y: ys[i]! }
    return { x: c.x, y: (fwd[i]! + bwd[i]!) / 2 }
  })
}

function downsamplePathCoords(
  coords: ReadonlyArray<{ x: number; y: number }>,
  maxKnots: number,
): { x: number; y: number }[] {
  if (coords.length <= maxKnots) return coords.map((c) => ({ x: c.x, y: c.y }))
  const n = coords.length
  const segments = maxKnots - 1
  const out: { x: number; y: number }[] = []
  for (let j = 0; j < maxKnots; j++) {
    const idx = Math.round((j / segments) * (n - 1))
    const c = coords[idx]!
    out.push({ x: c.x, y: c.y })
  }
  return out.filter(
    (p, i, a) => i === 0 || (p.x !== a[i - 1]!.x || p.y !== a[i - 1]!.y),
  )
}

/**
 * Approximating cubic path: smoothed + downsampled knots, not through every raw sample.
 */
function smoothLinePath(
  points: ReadonlyArray<{ x: number; y: number }>,
  catmullDivisor: number = CHART_CATMULL_DIVISOR,
): string {
  if (points.length === 0) return ''
  const first = points[0]!
  if (points.length === 1) return `M ${first.x} ${first.y}`
  if (points.length === 2) {
    const second = points[1]!
    return `M ${first.x} ${first.y} L ${second.x} ${second.y}`
  }
  const k = catmullDivisor
  let d = `M ${first.x} ${first.y}`
  for (let i = 0; i < points.length - 1; i++) {
    const p0 = points[Math.max(0, i - 1)]!
    const p1 = points[i]!
    const p2 = points[i + 1]!
    const p3 = points[Math.min(points.length - 1, i + 2)]!
    const cp1x = p1.x + (p2.x - p0.x) / k
    const cp1y = p1.y + (p2.y - p0.y) / k
    const cp2x = p2.x - (p3.x - p1.x) / k
    const cp2y = p2.y - (p3.y - p1.y) / k
    d += ` C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${p2.x} ${p2.y}`
  }
  return d
}

function buildApproxSparklinePath(
  rawCoords: ReadonlyArray<{ x: number; y: number }>,
): string {
  if (rawCoords.length === 0) return ''
  const softened = softenChartCoords(rawCoords, CHART_Y_SMOOTH_ALPHA)
  const knots = downsamplePathCoords(softened, CHART_PATH_MAX_KNOTS)
  return smoothLinePath(knots)
}

function WinRateProgressionChart({
  points,
  label,
}: {
  points: CumulativeWinRatePoint[]
  label: string
}) {
  const innerW = CHART_W - 2 * CHART_PAD_X
  const innerH = CHART_H - 2 * CHART_PAD_Y

  if (points.length === 0) {
    return (
      <div
        className="h-full min-h-[48px] w-full rounded-md bg-gray-50/70 border border-dashed border-gray-200"
        aria-hidden
      />
    )
  }

  const n = points.length
  const times = points.map((p) => p.exitTime)
  const tMin = times[0]!
  const tMax = times[n - 1]!
  const spread = tMax - tMin

  const coords = points.map((p, i) => {
    let xNorm: number
    if (spread > 0) {
      xNorm = (p.exitTime - tMin) / spread
    } else if (n > 1) {
      xNorm = i / (n - 1)
    } else {
      xNorm = 0.5
    }
    const x = CHART_PAD_X + xNorm * innerW
    const rate = Math.min(100, Math.max(0, p.cumulativeWinRatePct))
    const y = CHART_PAD_Y + (1 - rate / 100) * innerH
    return { x, y }
  })

  const lineD = buildApproxSparklinePath(coords)

  return (
    <svg
      className="block h-full w-full"
      viewBox={`0 0 ${CHART_W} ${CHART_H}`}
      preserveAspectRatio="none"
      role="img"
      aria-label={label}
    >
      <path
        d={lineD}
        fill="none"
        stroke="rgb(234 88 12)"
        strokeWidth={CHART_STROKE_WIDTH}
        strokeLinejoin="round"
        strokeLinecap="round"
      />
    </svg>
  )
}

function ProfitFactorProgressionChart({
  points,
  label,
}: {
  points: CumulativeProfitFactorPoint[]
  label: string
}) {
  const innerW = CHART_W - 2 * CHART_PAD_X
  const innerH = CHART_H - 2 * CHART_PAD_Y

  if (points.length === 0) {
    return (
      <div
        className="h-full min-h-[48px] w-full rounded-md border border-dashed border-gray-200 bg-gray-50/70"
        aria-hidden
      />
    )
  }

  const raws = points.map((p) => p.cumulativeProfitFactor)
  const finiteMax = Math.max(
    0,
    ...raws.filter((v): v is number => Number.isFinite(v)),
  )
  const infinityCap = finiteMax > 0 ? finiteMax * 1.18 : 10

  const displayVals = raws.map((v) =>
    Number.isFinite(v) ? Math.max(0, v) : Math.max(infinityCap, 10),
  )
  const yMax = Math.max(...displayVals, 0.01)

  const n = points.length
  const times = points.map((p) => p.exitTime)
  const tMin = times[0]!
  const tMax = times[n - 1]!
  const spread = tMax - tMin

  const coords = points.map((p, i) => {
    let xNorm: number
    if (spread > 0) {
      xNorm = (p.exitTime - tMin) / spread
    } else if (n > 1) {
      xNorm = i / (n - 1)
    } else {
      xNorm = 0.5
    }
    const x = CHART_PAD_X + xNorm * innerW
    const dv = displayVals[i]!
    const y = CHART_PAD_Y + (1 - dv / yMax) * innerH
    return { x, y }
  })

  const lineD = buildApproxSparklinePath(coords)

  return (
    <svg
      className="block h-full w-full"
      viewBox={`0 0 ${CHART_W} ${CHART_H}`}
      preserveAspectRatio="none"
      role="img"
      aria-label={label}
    >
      <path
        d={lineD}
        fill="none"
        stroke="rgb(234 88 12)"
        strokeWidth={CHART_STROKE_WIDTH}
        strokeLinejoin="round"
        strokeLinecap="round"
      />
    </svg>
  )
}
